const API_BASE = "/api";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function fetchWithAuth<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    credentials: "include", // Send cookies
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (response.status === 401) {
    // Redirect to login if not authenticated
    window.location.href = "/login";
    throw new ApiError(401, "Unauthorized");
  }

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Unknown error" }));
    throw new ApiError(
      response.status,
      error.detail || `HTTP ${response.status}`,
    );
  }

  return response.json();
}

// Types
export interface DashboardMetrics {
  metrics: {
    total_mrr: string;
    total_arr: string;
    active_customers: number;
    new_this_month: number;
    revenue_this_month: Array<{
      currency: string;
      gross: string;
      fees: string;
      net: string;
      payment_count: number;
    }>;
    other_fees_this_month: Array<{ currency: string; amount: string }>;
    paying_customers_90d: number;
    ai_costs_month_usd: number;
    by_service: Array<{
      code: string;
      name: string;
      customer_count: number;
      mrr: string;
      revenue: Array<{ currency: string; gross: string }>;
    }>;
  };
  recent_payments: Array<{
    id: string;
    amount: string;
    currency: string;
    status: string;
    paid_at: string | null;
    customer_email: string;
    customer_name: string | null;
    service_code: string;
    service_name: string;
  }>;
}

// Organization (renamed from Customer for B2B clarity)
export interface Organization {
  id: string;
  email: string | null; // Legacy, nullable - use contacts for primary contact
  name: string | null;
  company_name: string | null;
  company_id: string | null; // IČO
  vat_id: string | null; // DIČ
  billing_email: string | null;
  stripe_customer_id: string | null;
  is_active: boolean;
  deactivated_at: string | null;
  created_at: string;
  updated_at: string;
}

// Backwards compatibility alias
export type Customer = Organization;

export interface OrganizationServiceInfo {
  service_code: string;
  service_name: string;
  plan_name: string | null;
  status: string;
  mrr: string;
}

export interface OrganizationAccountInfo {
  service_code: string;
  account_name: string | null;
  external_account_id: string;
}

export interface SpendInfo {
  currency: string;
  amount: string;
}

export interface OrganizationListItem {
  id: string;
  email: string;
  name: string | null;
  company_name: string | null;
  is_active: boolean;
  deactivated_at: string | null;
  services: OrganizationServiceInfo[];
  total_mrr: string;
  created_at: string;
  accounts: OrganizationAccountInfo[];
  credits_balance: number;
  lifetime_spend: SpendInfo[];
  last_activity_at: string | null;
  last_activity_type: "payment" | "ai_chat" | "import" | "signup" | null;
  import_status: "ok" | "failed" | null;
}

export interface OrganizationListResponse {
  organizations: OrganizationListItem[];
  total: number;
  page: number;
  per_page: number;
}

// Backwards compatibility alias
export type CustomerListResponse = OrganizationListResponse;

export interface Subscription {
  id: string;
  service_code: string;
  service_name: string;
  plan_code: string | null;
  plan_name: string | null;
  status: string;
  billing_interval: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  limits: Record<string, number | boolean> | null;
}

export interface CreditBalance {
  id: string;
  service_code: string;
  service_name: string;
  external_account_id: string;
  account_name: string | null;
  balance: number;
  lifetime_purchased: number;
  lifetime_used: number;
}

export interface CreditTransaction {
  id: string;
  created_at: string;
  amount: number;
  balance_after: number;
  transaction_type:
    "purchase" | "usage" | "bonus" | "refund" | "correction" | "expiry";
  description: string | null;
  reference_type: string | null;
  reference_id: string | null;
  resource_name: string | null;
}

export interface Payment {
  id: string;
  amount: string;
  currency: string;
  status: string;
  paid_at: string | null;
  description: string | null;
  stripe_invoice_url: string | null;
  fakturoid_invoice_url: string | null;
  service_code: string;
  service_name: string;
  created_at: string;
  fee_amount: string | null;
  fee_currency: string | null;
}

export interface AcquisitionData {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer_url: string | null;
  landing_page: string | null;
  gclid: string | null;
  fbclid: string | null;
  device_type: string | null;
}

export interface ServiceAccount {
  id: string;
  service_code: string;
  service_name: string;
  external_account_id: string;
  account_name: string | null;
  organization_id: string | null;
  first_touch: AcquisitionData | null;
  last_touch: AcquisitionData | null;
  created_at: string;
}

export interface ServiceOrganization {
  id: string;
  service_code: string;
  service_name: string;
  external_organization_id: string;
  organization_name: string | null;
  account_count: number;
  created_at: string;
}

export interface OrganizationDetail extends Organization {
  subscriptions: Subscription[];
  credit_balances: CreditBalance[];
  payments: Payment[];
  service_accounts: ServiceAccount[];
  service_organizations: ServiceOrganization[];
}

// Backwards compatibility alias
export type CustomerDetail = OrganizationDetail;

// API functions
export async function getDashboard(): Promise<DashboardMetrics> {
  return fetchWithAuth<DashboardMetrics>("/admin/dashboard");
}

export async function getOrganizations(params: {
  page?: number;
  per_page?: number;
  search?: string;
  service_code?: string;
  status?: string;
  include_inactive?: boolean;
}): Promise<OrganizationListResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set("page", params.page.toString());
  if (params.per_page) searchParams.set("per_page", params.per_page.toString());
  if (params.search) searchParams.set("search", params.search);
  if (params.service_code)
    searchParams.set("service_code", params.service_code);
  if (params.status) searchParams.set("status", params.status);
  if (params.include_inactive) searchParams.set("include_inactive", "true");

  const query = searchParams.toString();
  return fetchWithAuth<OrganizationListResponse>(
    `/admin/organizations${query ? `?${query}` : ""}`,
  );
}

export async function getOrganization(id: string): Promise<OrganizationDetail> {
  return fetchWithAuth<OrganizationDetail>(`/admin/organizations/${id}`);
}

/** @deprecated Use getOrganizations() instead */
export const getCustomers = getOrganizations;
/** @deprecated Use getOrganization() instead */
export const getCustomer = getOrganization;

// =============================================================================
// Services Admin
// =============================================================================

export interface PlanListItem {
  id: string;
  code: string;
  name: string;
  price_monthly: string;
  price_yearly: string;
  currency: string;
  limits: Record<string, number | boolean>;
  features: Record<string, number | boolean | string>;
  stripe_product_id: string | null;
  stripe_price_monthly_id: string | null;
  stripe_price_yearly_id: string | null;
  is_active: boolean;
  is_recommended: boolean;
  subscription_count: number;
}

export interface CreditPackListItem {
  id: string;
  code: string;
  name: string;
  credits: number;
  price: string;
  currency: string;
  is_active: boolean;
}

export interface ServiceListItem {
  id: string;
  code: string;
  name: string;
  billing_type: string;
  is_active: boolean;
  callback_enabled: boolean;
  plan_count: number;
  subscription_count: number;
  mrr: string;
}

export interface ServiceDetail extends ServiceListItem {
  stripe_statement_descriptor: string | null;
  callback_url: string | null;
  created_at: string;
  plans: PlanListItem[];
  credit_packs: CreditPackListItem[];
}

export interface IntegrationInfo {
  service_code: string;
  service_name: string;
  api_key_header: string;
  api_key_example: string;
  callback_url: string | null;
  callback_enabled: boolean;
  base_url: string;
  endpoints: Array<{ method: string; path: string; description: string }>;
  documentation_links: Array<{ label: string; url: string }>;
  flow_description: string;
}

export async function getServices(): Promise<ServiceListItem[]> {
  return fetchWithAuth<ServiceListItem[]>("/admin/services");
}

export async function getService(id: string): Promise<ServiceDetail> {
  return fetchWithAuth<ServiceDetail>(`/admin/services/${id}`);
}

export async function getIntegrationInfo(id: string): Promise<IntegrationInfo> {
  return fetchWithAuth<IntegrationInfo>(`/admin/services/${id}/integration`);
}

export async function updatePlan(
  serviceId: string,
  planId: string,
  data: Partial<PlanListItem>,
): Promise<PlanListItem> {
  return fetchWithAuth<PlanListItem>(
    `/admin/services/${serviceId}/plans/${planId}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

export async function updateServiceCallback(
  serviceId: string,
  data: {
    callback_url?: string;
    callback_secret?: string;
    callback_enabled?: boolean;
  },
): Promise<ServiceDetail> {
  return fetchWithAuth<ServiceDetail>(`/admin/services/${serviceId}/callback`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// =============================================================================
// Plan Prices (Multi-currency)
// =============================================================================

export interface PlanPrice {
  id: string;
  plan_id: string;
  currency: string;
  interval: string; // monthly, yearly
  amount: string;
  stripe_price_id: string | null;
  is_active: boolean;
}

export interface PlanPriceCreate {
  currency: string;
  interval: string;
  amount: string;
  stripe_price_id?: string | null;
  is_active?: boolean;
}

export interface PlanPriceUpdate {
  amount?: string;
  stripe_price_id?: string | null;
  is_active?: boolean;
}

export async function getPlanPrices(
  serviceId: string,
  planId: string,
): Promise<PlanPrice[]> {
  return fetchWithAuth<PlanPrice[]>(
    `/admin/services/${serviceId}/plans/${planId}/prices`,
  );
}

export async function createPlanPrice(
  serviceId: string,
  planId: string,
  data: PlanPriceCreate,
): Promise<PlanPrice> {
  return fetchWithAuth<PlanPrice>(
    `/admin/services/${serviceId}/plans/${planId}/prices`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
}

export async function updatePlanPrice(
  serviceId: string,
  planId: string,
  priceId: string,
  data: PlanPriceUpdate,
): Promise<PlanPrice> {
  return fetchWithAuth<PlanPrice>(
    `/admin/services/${serviceId}/plans/${planId}/prices/${priceId}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

export async function deletePlanPrice(
  serviceId: string,
  planId: string,
  priceId: string,
): Promise<void> {
  await fetch(
    `${API_BASE}/admin/services/${serviceId}/plans/${planId}/prices/${priceId}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );
}

// =============================================================================
// Feature Definitions
// =============================================================================

export interface FeatureDefinition {
  id: string;
  code: string;
  group_code: string;
  group_label_key: string;
  feature_label_key: string;
  feature_description_key: string | null;
  sort_order: number;
  is_highlight: boolean;
}

export interface FeatureDefinitionCreate {
  code: string;
  group_code: string;
  group_label_key: string;
  feature_label_key: string;
  feature_description_key?: string | null;
  sort_order?: number;
  is_highlight?: boolean;
}

export interface FeatureDefinitionUpdate {
  code?: string;
  group_code?: string;
  group_label_key?: string;
  feature_label_key?: string;
  feature_description_key?: string | null;
  sort_order?: number;
  is_highlight?: boolean;
}

export interface FeatureGroup {
  group_code: string;
  group_label_key: string;
  features: FeatureDefinition[];
}

export async function getFeatureDefinitions(
  serviceId: string,
): Promise<FeatureDefinition[]> {
  return fetchWithAuth<FeatureDefinition[]>(
    `/admin/services/${serviceId}/feature-definitions`,
  );
}

export async function createFeatureDefinition(
  serviceId: string,
  data: FeatureDefinitionCreate,
): Promise<FeatureDefinition> {
  return fetchWithAuth<FeatureDefinition>(
    `/admin/services/${serviceId}/feature-definitions`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
}

export async function updateFeatureDefinition(
  serviceId: string,
  featureId: string,
  data: FeatureDefinitionUpdate,
): Promise<FeatureDefinition> {
  return fetchWithAuth<FeatureDefinition>(
    `/admin/services/${serviceId}/feature-definitions/${featureId}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

export async function deleteFeatureDefinition(
  serviceId: string,
  featureId: string,
): Promise<void> {
  await fetch(
    `${API_BASE}/admin/services/${serviceId}/feature-definitions/${featureId}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );
}

export async function reorderFeatureDefinitions(
  serviceId: string,
  items: Array<{ id: string; sort_order: number }>,
): Promise<FeatureDefinition[]> {
  return fetchWithAuth<FeatureDefinition[]>(
    `/admin/services/${serviceId}/feature-definitions/reorder`,
    {
      method: "PUT",
      body: JSON.stringify({ items }),
    },
  );
}

/** Group feature definitions by group_code */
export function groupFeatureDefinitions(
  features: FeatureDefinition[],
): FeatureGroup[] {
  const groups = new Map<string, FeatureGroup>();

  for (const feature of features) {
    if (!groups.has(feature.group_code)) {
      groups.set(feature.group_code, {
        group_code: feature.group_code,
        group_label_key: feature.group_label_key,
        features: [],
      });
    }
    groups.get(feature.group_code)!.features.push(feature);
  }

  return Array.from(groups.values());
}

// =============================================================================
// CRM Module
// =============================================================================

export type LeadSource =
  | "direct"
  | "referral"
  | "website"
  | "producthunt"
  | "linkedin"
  | "cold_outreach"
  | "other";
export type LeadWarmth = "hot" | "warm" | "cold";
export type LeadStage =
  | "new"
  | "contacted"
  | "demo_scheduled"
  | "demo_done"
  | "negotiation"
  | "won"
  | "lost";
export type ActivityType =
  "note" | "call" | "email" | "meeting" | "task" | "demo";

export interface Product {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
}

export interface Lead {
  id: string;
  email: string;
  name: string;
  company: string | null;
  phone: string | null;
  source: LeadSource;
  warmth: LeadWarmth;
  team_size: number | null;
  stage: LeadStage;
  probability: number;
  expected_value: string | null;
  expected_close_date: string | null;
  target_service_code: string | null;
  customer_id: string | null;
  converted_at: string | null;
  lost_reason: string | null;
  created_at: string;
  updated_at: string;
  products?: Product[];
  activities_count?: number;
  last_activity_at?: string | null;
  activities?: Activity[];
}

export interface Activity {
  id: string;
  lead_id: string | null;
  customer_id: string | null;
  type: ActivityType;
  title: string;
  content: string | null;
  due_date: string | null;
  completed_at: string | null;
  created_by: string;
  created_at: string;
  is_overdue: boolean;
}

export interface PipelineStageStats {
  stage: LeadStage;
  count: number;
  total_value: string;
}

export interface CrmDashboard {
  total_leads: number;
  open_leads: number;
  leads_by_stage: PipelineStageStats[];
  leads_by_warmth: Record<string, number>;
  conversion_rate: number;
  avg_deal_value: string | null;
  tasks_due_today: number;
  tasks_overdue: number;
}

export interface LeadCreate {
  email: string;
  name: string;
  company?: string;
  phone?: string;
  source?: LeadSource;
  warmth?: LeadWarmth;
  team_size?: number;
  target_service_code?: string;
  expected_value?: string;
  product_ids?: string[];
}

export interface LeadUpdate {
  email?: string;
  name?: string;
  company?: string;
  phone?: string;
  source?: LeadSource;
  warmth?: LeadWarmth;
  team_size?: number;
  stage?: LeadStage;
  probability?: number;
  expected_value?: string;
  expected_close_date?: string;
  target_service_code?: string;
  lost_reason?: string;
  product_ids?: string[];
}

export interface ActivityCreate {
  lead_id?: string;
  customer_id?: string;
  type?: ActivityType;
  title: string;
  content?: string;
  due_date?: string;
}

// CRM API Functions

export async function getLeads(params?: {
  stage?: LeadStage;
  warmth?: LeadWarmth;
  product_id?: string;
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<Lead[]> {
  const searchParams = new URLSearchParams();
  if (params?.stage) searchParams.set("stage", params.stage);
  if (params?.warmth) searchParams.set("warmth", params.warmth);
  if (params?.product_id) searchParams.set("product_id", params.product_id);
  if (params?.search) searchParams.set("search", params.search);
  if (params?.page) searchParams.set("page", params.page.toString());
  if (params?.per_page)
    searchParams.set("per_page", params.per_page.toString());

  const url = `/admin/leads${searchParams.toString() ? `?${searchParams}` : ""}`;
  return fetchWithAuth(url);
}

export async function getLead(id: string): Promise<Lead> {
  return fetchWithAuth(`/admin/leads/${id}`);
}

export async function createLead(data: LeadCreate): Promise<Lead> {
  return fetchWithAuth("/admin/leads", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateLead(id: string, data: LeadUpdate): Promise<Lead> {
  return fetchWithAuth(`/admin/leads/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteLead(id: string): Promise<void> {
  await fetch(`${API_BASE}/admin/leads/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
}

export async function convertLead(
  id: string,
  customerId: string,
): Promise<Lead> {
  return fetchWithAuth(`/admin/leads/${id}/convert`, {
    method: "POST",
    body: JSON.stringify({ customer_id: customerId }),
  });
}

export async function getCrmDashboard(): Promise<CrmDashboard> {
  return fetchWithAuth("/admin/leads/dashboard");
}

export async function getProducts(activeOnly = true): Promise<Product[]> {
  return fetchWithAuth(`/admin/products?active_only=${activeOnly}`);
}

export async function getActivities(params?: {
  lead_id?: string;
  customer_id?: string;
  type?: ActivityType;
  tasks_only?: boolean;
  include_completed?: boolean;
}): Promise<Activity[]> {
  const searchParams = new URLSearchParams();
  if (params?.lead_id) searchParams.set("lead_id", params.lead_id);
  if (params?.customer_id) searchParams.set("customer_id", params.customer_id);
  if (params?.type) searchParams.set("type", params.type);
  if (params?.tasks_only) searchParams.set("tasks_only", "true");
  if (params?.include_completed === false)
    searchParams.set("include_completed", "false");

  const url = `/admin/activities${searchParams.toString() ? `?${searchParams}` : ""}`;
  return fetchWithAuth(url);
}

export async function getTasksDue(includeOverdue = true): Promise<Activity[]> {
  return fetchWithAuth(
    `/admin/activities/tasks/due?include_overdue=${includeOverdue}`,
  );
}

export async function createActivity(data: ActivityCreate): Promise<Activity> {
  return fetchWithAuth("/admin/activities", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function completeTask(id: string): Promise<Activity> {
  return fetchWithAuth(`/admin/activities/${id}/complete`, { method: "POST" });
}

export async function deleteActivity(id: string): Promise<void> {
  await fetch(`${API_BASE}/admin/activities/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
}

// =============================================================================
// Menu Editor Usage
// =============================================================================

export interface MenuEditorImport {
  id: string;
  import_id: string;
  account_id: string;
  account_name: string;
  import_timestamp: string;
  duration_sec: number;
  tokens_input: number;
  tokens_output: number;
  cost_usd: number;
  model: string;
  source_files: string[];
  success: boolean;
  created_at: string;
}

export interface MenuEditorImportListItem {
  id: string;
  import_id: string;
  account_id: string;
  account_name: string;
  import_timestamp: string;
  cost_usd: number;
  success: boolean;
}

export interface MenuEditorUsageSummary {
  total_imports: number;
  successful_imports: number;
  failed_imports: number;
  total_cost_usd: number;
  avg_cost_usd: number;
  total_tokens_input: number;
  total_tokens_output: number;
  avg_duration_sec: number;
}

export interface MenuEditorAccount {
  account_id: string;
  account_name: string;
  import_count: number;
}

// Menu Editor Usage API Functions

export async function getMenuEditorImports(params?: {
  account_id?: string;
  success?: boolean;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}): Promise<MenuEditorImportListItem[]> {
  const searchParams = new URLSearchParams();
  if (params?.account_id) searchParams.set("account_id", params.account_id);
  if (params?.success !== undefined)
    searchParams.set("success", String(params.success));
  if (params?.date_from) searchParams.set("date_from", params.date_from);
  if (params?.date_to) searchParams.set("date_to", params.date_to);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.per_page) searchParams.set("per_page", String(params.per_page));
  const query = searchParams.toString();
  return fetchWithAuth(`/admin/menu-editor/imports${query ? `?${query}` : ""}`);
}

export async function getMenuEditorImport(
  importId: string,
): Promise<MenuEditorImport> {
  return fetchWithAuth(`/admin/menu-editor/imports/${importId}`);
}

export async function getMenuEditorSummary(params?: {
  account_id?: string;
  date_from?: string;
  date_to?: string;
}): Promise<MenuEditorUsageSummary> {
  const searchParams = new URLSearchParams();
  if (params?.account_id) searchParams.set("account_id", params.account_id);
  if (params?.date_from) searchParams.set("date_from", params.date_from);
  if (params?.date_to) searchParams.set("date_to", params.date_to);
  const query = searchParams.toString();
  return fetchWithAuth(`/admin/menu-editor/summary${query ? `?${query}` : ""}`);
}

export async function getMenuEditorAccounts(): Promise<MenuEditorAccount[]> {
  return fetchWithAuth("/admin/menu-editor/accounts");
}

// =============================================================================
// Menu Editor Overview
// =============================================================================

export interface MESpendItem {
  currency: string;
  amount: string;
}

export interface MEOverviewAccount {
  account_name: string | null;
  owner_email: string;
  organization_id: string;
  import_status: "ok" | "failed" | null;
  credits_balance: number;
  conversations_30d: number;
  ai_cost_30d_usd: number;
  lifetime_spend: MESpendItem[];
}

export interface MENewAccount {
  account_name: string | null;
  owner_email: string;
  organization_id: string;
  signup_at: string;
  import_status: "ok" | "failed" | null;
  import_at: string | null;
  first_ai_at: string | null;
  purchased: boolean;
}

export interface MERecentPayment {
  paid_at: string | null;
  email: string;
  description: string | null;
  amount: string;
  currency: string;
  status: string;
  fakturoid_invoice_url: string | null;
}

export interface MenuEditorOverview {
  revenue_this_month: Array<{
    currency: string;
    gross: string;
    fees: string;
    net: string;
    payment_count: number;
  }>;
  other_fees_this_month: Array<{ currency: string; amount: string }>;
  ai_chat_cost_month_usd: number;
  ai_import_cost_month_usd: number;
  credits_outstanding: number;
  credits_liability_usd: number | null;
  imports_30d_ok: number;
  imports_30d_failed: number;
  active_accounts_30d: number;
  top_accounts: MEOverviewAccount[];
  new_accounts: MENewAccount[];
  recent_payments: MERecentPayment[];
}

export async function getMenuEditorOverview(): Promise<MenuEditorOverview> {
  return fetchWithAuth("/admin/menu-editor/overview");
}

// =============================================================================
// AI Chat Costs
// =============================================================================

export interface AICostsSummary {
  total_cost_usd: string;
  total_conversations: number;
  total_accounts: number;
  period_from: string | null;
  period_to: string | null;
}

export interface AccountCostItem {
  account_id: string;
  account_name: string;
  cost_usd: string;
  conversations: number;
}

export interface UserCostItem {
  user_id: string;
  user_email: string;
  account_name: string;
  cost_usd: string;
  conversations: number;
}

export interface AICostsAccount {
  account_id: string;
  account_name: string;
  conversation_count: number;
}

export async function getAICostsSummary(params?: {
  date_from?: string;
  date_to?: string;
  account_id?: string;
  context_type?: string;
}): Promise<AICostsSummary> {
  const searchParams = new URLSearchParams();
  if (params?.date_from) searchParams.set("date_from", params.date_from);
  if (params?.date_to) searchParams.set("date_to", params.date_to);
  if (params?.account_id) searchParams.set("account_id", params.account_id);
  if (params?.context_type)
    searchParams.set("context_type", params.context_type);
  const query = searchParams.toString();
  return fetchWithAuth(`/admin/ai-costs/summary${query ? `?${query}` : ""}`);
}

export async function getAICostsByAccount(params?: {
  date_from?: string;
  date_to?: string;
  context_type?: string;
}): Promise<AccountCostItem[]> {
  const searchParams = new URLSearchParams();
  if (params?.date_from) searchParams.set("date_from", params.date_from);
  if (params?.date_to) searchParams.set("date_to", params.date_to);
  if (params?.context_type)
    searchParams.set("context_type", params.context_type);
  const query = searchParams.toString();
  return fetchWithAuth(`/admin/ai-costs/by-account${query ? `?${query}` : ""}`);
}

export async function getAICostsByUser(params?: {
  date_from?: string;
  date_to?: string;
  account_id?: string;
  context_type?: string;
}): Promise<UserCostItem[]> {
  const searchParams = new URLSearchParams();
  if (params?.date_from) searchParams.set("date_from", params.date_from);
  if (params?.date_to) searchParams.set("date_to", params.date_to);
  if (params?.account_id) searchParams.set("account_id", params.account_id);
  if (params?.context_type)
    searchParams.set("context_type", params.context_type);
  const query = searchParams.toString();
  return fetchWithAuth(`/admin/ai-costs/by-user${query ? `?${query}` : ""}`);
}

export async function getAICostsAccounts(): Promise<AICostsAccount[]> {
  return fetchWithAuth("/admin/ai-costs/accounts");
}

// =============================================================================
// AI Analytics (from Menu Editor)
// =============================================================================

export interface AIAnalyticsOverview {
  total_conversations: number;
  total_suggestions: number;
  suggestions_accepted: number;
  suggestions_rejected: number;
  acceptance_rate: number;
  total_cost_usd: number;
  total_tokens_in: number;
  total_tokens_out: number;
  avg_turns_per_conversation: number;
  conversations_with_screenshots: number;
}

export interface TopicBreakdown {
  topic: string;
  count: number;
  percentage: number;
}

export interface ProblematicSetting {
  setting_path: string;
  total_suggestions: number;
  accepted: number;
  rejected: number;
  rejection_rate: number;
}

export interface AIAnalyticsSummary {
  overview: AIAnalyticsOverview;
  topics: TopicBreakdown[];
  problematic_settings: ProblematicSetting[];
  period_start: string;
  period_end: string;
}

export interface ConversationListItem {
  id: string;
  created_at: string;
  user_message: string | null;
  topic_category: string | null;
  resolution_type: string | null;
  suggestions_count: number;
  suggestions_accepted: number;
  suggestions_rejected: number;
  outcome: string | null;
  cost_usd: number | null;
  session_turns: number;
  user_email: string;
  account_name: string;
}

export interface ConversationListResponse {
  items: ConversationListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface ConversationMessage {
  role: string;
  content: string;
  has_screenshot: boolean;
  sequence_num: number;
}

export interface SuggestionDetail {
  id: string;
  setting_path: string;
  old_value: unknown;
  new_value: unknown;
  explanation: string | null;
  status: string;
}

export interface ConversationDetail {
  id: string;
  created_at: string;
  user_message: string | null;
  topic_category: string | null;
  resolution_type: string | null;
  suggestions_count: number;
  suggestions_accepted: number;
  suggestions_rejected: number;
  outcome: string | null;
  cost_usd: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  session_turns: number;
  initial_settings_snapshot: Record<string, unknown> | null;
  messages: ConversationMessage[];
  suggestions: SuggestionDetail[];
}

export interface ConversationListParams {
  page?: number;
  page_size?: number;
  topic?: string;
  resolution?: string;
  days?: number;
}

export async function getAIAnalyticsSummary(
  days = 30,
): Promise<AIAnalyticsSummary> {
  return fetchWithAuth(`/admin/ai-analytics/summary?days=${days}`);
}

export async function getAIAnalyticsConversations(
  params: ConversationListParams = {},
): Promise<ConversationListResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set("page", String(params.page));
  if (params.page_size) searchParams.set("page_size", String(params.page_size));
  if (params.topic) searchParams.set("topic", params.topic);
  if (params.resolution) searchParams.set("resolution", params.resolution);
  if (params.days) searchParams.set("days", String(params.days));
  const query = searchParams.toString();
  return fetchWithAuth(
    `/admin/ai-analytics/conversations${query ? `?${query}` : ""}`,
  );
}

export async function getAIAnalyticsConversationDetail(
  id: string,
): Promise<ConversationDetail> {
  return fetchWithAuth(`/admin/ai-analytics/conversations/${id}`);
}

// =============================================================================
// Credits Management
// =============================================================================

export type CreditAdjustmentType = "bonus" | "correction" | "refund";

export interface CreditAdjustmentRequest {
  amount: number;
  type: CreditAdjustmentType;
  description: string;
  idempotency_key: string;
}

export interface CreditAdjustmentResponse {
  success: boolean;
  customer_id: string;
  service_code: string;
  new_balance: number;
  transaction_id: string;
}

export async function addCredits(
  creditBalanceId: string,
  data: CreditAdjustmentRequest,
): Promise<CreditAdjustmentResponse> {
  return fetchWithAuth<CreditAdjustmentResponse>(
    `/admin/credits/balance/${creditBalanceId}`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
}

export async function getCreditTransactions(
  serviceCode: string,
  externalAccountId: string,
  limit: number = 10,
  offset: number = 0,
): Promise<CreditTransaction[]> {
  const params = new URLSearchParams({
    service_code: serviceCode,
    external_account_id: externalAccountId,
    limit: limit.toString(),
    offset: offset.toString(),
  });
  return fetchWithAuth<CreditTransaction[]>(
    `/admin/credits/transactions?${params}`,
  );
}

// =============================================================================
// Admin Users (multi-admin)
// =============================================================================

export interface AdminUserListItem {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface CreateAdminUserRequest {
  email: string;
  name: string;
  password?: string | null;
}

export interface CreateAdminUserResponse {
  id: string;
  email: string;
  name: string;
  generated_password: string | null;
}

export async function getAdminUsers(): Promise<AdminUserListItem[]> {
  return fetchWithAuth<AdminUserListItem[]>("/admin/users");
}

export async function createAdminUser(
  data: CreateAdminUserRequest,
): Promise<CreateAdminUserResponse> {
  return fetchWithAuth<CreateAdminUserResponse>("/admin/users", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAdminUser(
  id: string,
  data: { is_active?: boolean },
): Promise<AdminUserListItem> {
  return fetchWithAuth<AdminUserListItem>(`/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function changeAdminPassword(data: {
  current_password: string;
  new_password: string;
}): Promise<{ message: string }> {
  return fetchWithAuth<{ message: string }>("/admin/auth/change-password", {
    method: "POST",
    body: JSON.stringify(data),
  });
}
